import { immerable } from "@/app/lib/system/immer-wrapper";
// my imports:
import WordModule from "@/app/lib/game-state/word-module";
import LetterModule from "@/app/lib/game-state/letter-module";
import { AbstractGameState } from "@/app/lib/game-state/abstract-game-state";
import { GameMode } from "@/app/lib/game-state/game-types";
import { PyramidData } from "@/app/lib/system/puzzle-generator";
import * as Util from "@/app/lib/system/util";

export type PyramidSnapshot = {
    seedWord: string;
    wildcards: string[],
    words: string[];
    solved: Array<{
        wordIndex: number,
        solution: string
    }>
    ingredients: string[];
    solutions: string[];
    usedSolutions: string[];
    victorious: boolean;
    completed: boolean;
    version: string;
}

export class PyramidGameState extends AbstractGameState<PyramidData, PyramidSnapshot> {
    name: GameMode = 'pyramid';
    seedWord: string ='';
    wildcards: string[] = [];
    solutions: string[] = [];

    constructor(version: string) {
        super(version);
    }

    protected wipeSpecific(): void {
        this.seedWord = '';
        this.wildcards = [];
        this.solutions = [];
        this.usedSolutions = [];
    }

    buildPuzzle(puzzleData: PyramidData): void {
        this.wipe();
        this.seedWord = puzzleData.seedWord;
        this.wildcards = puzzleData.wildcards;
        // shuffle the ingredients that are incoming
        this.ingredients = Util.shuffle(puzzleData.ingredients);
        this.solutions = puzzleData.solutions;

        // sort words by size
        puzzleData.words.sort((a, b) => a.length - b.length);

        for (const word of puzzleData.words) {
            this.addNewWord(word);
        }
    }

    exportSnapshot(): PyramidSnapshot {
        // convert wordmodules to word strings
        // record solved states
        const words: string[] = [];
        const solved: Array<{
            wordIndex: number,
            solution: string
        }> = [];
        for (const wordModule of this.words) {
            words.push(wordModule.stringifyComplete());
            if (wordModule.solved) {
                solved.push({
                    wordIndex: wordModule.index,
                    solution: wordModule.stringifyState()
                })
            }
        }
        const snapshot: PyramidSnapshot = {
            seedWord: this.seedWord,
            wildcards: this.wildcards,
            words: words,
            solved: solved,
            ingredients: this.ingredients,
            solutions: this.solutions,
            usedSolutions: this.usedSolutions,
            completed: this.completed,
            victorious: this.victorious,
            version: this.version
        }
        return snapshot;
        
    }

    importSnapshot(snapshot: PyramidSnapshot) : void {
        this.buildPuzzle({
            seedWord: snapshot.seedWord,
            wildcards: snapshot.wildcards,
            words: snapshot.words,
            ingredients: snapshot.ingredients,
            solutions: snapshot.solutions
        });

        // process any solved words
        for (const solvedWord of snapshot.solved) {
            const wordModule: WordModule = this.words[solvedWord.wordIndex];
            wordModule.import(solvedWord.solution);
            wordModule.solved = true;
        }
        
        this.seedWord = snapshot.seedWord;
        this.wildcards = snapshot.wildcards;
        this.usedSolutions = snapshot.usedSolutions;
        this.victorious = snapshot.victorious;
        this.completed = snapshot.completed;
        this.version = snapshot.version;
    }

    freezeInputs(): boolean {
        return this.victorious || this.completed;
    }

    validateWord(wordIndex: number): boolean {
        const wordModule: WordModule = this.words[wordIndex];
        // if the word is not complete, we're done here
        if (!wordModule.isComplete()) {
            return false;
        }

        const wordString: string = wordModule.stringifyState().toLowerCase();
        // we want solutions that are in potential solutions
        // BUT are not already used
        if (
            !this.usedSolutions.includes(wordString)
            && this.solutions.includes(wordString)
        ) {
            // add to used solutions
            this.usedSolutions.push(wordString);
            // mark solved
            wordModule.solved = true;
            // check for victory now that we marked a new solve
            this.checkVictory(true);
            return true;
        }
        // if it wasn't correct we clear entries
        else {
            return false;
        }       
    }

    reveal(trueVictory: boolean) {
        for (const wordModule of this.words) {
            // default option is to reveal the seedword
            let revealWord: string = wordModule.puzzleWord;
            // check if the seedword has been used elsewhere
            if (this.usedSolutions.includes(wordModule.puzzleWord.toLowerCase())) {
                // search for an alternate solution
                let alternateSolution: string = '';
                for (const candidateWord of this.solutions) {
                    if (
                        // can't be already used
                        !this.usedSolutions.includes(candidateWord)
                        && wordModule.validate(candidateWord, this.ingredients)
                    ) {
                        alternateSolution = candidateWord;
                        break;
                    }
                }
                if (alternateSolution !== '') {
                    revealWord = alternateSolution;
                }
            }
            wordModule.reveal(revealWord);
            this.usedSolutions.push(revealWord);
        }
        this.markCompleted(trueVictory);
    }

    revealWithAnimation({
        trueVictory,
        intervalDelay = 0,
        animationMap
    }: {
        trueVictory: boolean;
        intervalDelay?: number;
        animationMap?: Map<number, () => void>
    }) {
        intervalDelay = intervalDelay * 1000;

        const unsolvedWords: WordModule[] =
            this.words.filter(w => !w.solved);

        for (const [i, wordModule] of unsolvedWords.entries()) {
            // default option is to reveal the seedword
            let revealWord: string = wordModule.puzzleWord;
            // check if the seedword has been used elsewhere
            if (this.usedSolutions.includes(wordModule.puzzleWord.toLowerCase())) {
                // search for an alternate solution
                let alternateSolution: string = '';
                for (const candidateWord of this.solutions) {
                    if (
                        // can't be already used
                        !this.usedSolutions.includes(candidateWord)
                        && wordModule.validate(candidateWord, this.ingredients)
                    ) {
                        alternateSolution = candidateWord;
                        break;
                    }
                }
                if (alternateSolution !== '') {
                    revealWord = alternateSolution;
                }
            }
            if (animationMap) {
                setTimeout(() => {
                    wordModule.reveal(revealWord);
                    animationMap.get(wordModule.index)?.();
                }, intervalDelay * i);
            }
            wordModule.reveal(revealWord);
            this.usedSolutions.push(revealWord);
        }
        this.markCompleted(trueVictory);
    }

    static isPyramidSnapshot(obj: any): obj is PyramidSnapshot {
        if (typeof obj !== 'object' || obj === null)
            return false;

        // seedWord: string
        if (typeof obj.seedWord !== 'string')
            return false;

        // wildcards: string[]
        if (
            !Array.isArray(obj.wildcards) ||
            !obj.wildcards.every((w: any) => typeof w === 'string')
        ) {
            return false;
        }

        // words: string[]
        if (
            !Array.isArray(obj.words) ||
            !obj.words.every((w: any) => typeof w === 'string')
        ) {
            return false;
        }

        // solved: { wordIndex: number, solution: string }[]
        if (
            !Array.isArray(obj.solved) ||
            !obj.solved.every((s: any) =>
                typeof s === 'object' &&
                s !== null &&
                typeof s.wordIndex === 'number' &&
                typeof s.solution === 'string'
            )
        ) {
            return false;
        }

        // ingredients: string[]
        if (
            !Array.isArray(obj.ingredients) ||
            !obj.ingredients.every((i: any) => typeof i === 'string')
        ) {
            return false;
        }

        // solutions: string[]
        if (
            !Array.isArray(obj.solutions) ||
            !obj.solutions.every((sol: any) => typeof sol === 'string')
        ) {
            return false;
        }

        // usedSolutions: string[]
        if (
            !Array.isArray(obj.usedSolutions) ||
            !obj.usedSolutions.every((sol: any) => typeof sol === 'string')
        ) {
            return false;
        }

        // victorious: boolean
        if (typeof obj.victorious !== 'boolean')
            return false;

        // completed: boolean
        if (typeof obj.completed !== 'boolean')
            return false;

        // version: string
        if (typeof obj.version !== 'string')
            return false;

        return true;
    }

}